/**
 * Free Medical Category Edit Modal
 *
 * Modal for creating/editing a free medical treatment category.
 * Mirrors the functionality from A2/resources/views/free_medical/init.blade.php
 *
 * Features:
 * - Create new categories with automatic sort positioning
 * - Edit existing categories with sort order management
 * - Validation with error feedback
 * - Sort number cascade handling (when position changes)
 * - Confirmation dialogs before destructive actions
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  CategoryFormData,
  FreeMedicalTreatmentCategory,
  FreeMedicalInitData,
} from '@/pages/main/freeMedical/types';
import { freeMedicalCategorySchema } from '@/schemas/freeMedicalCategorySchema';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import { deepCompare } from '@/utils/deepCompare';
import {
  AppTextField,
  AppSelect,
  AppButton,
  AppModal,
  AppRow,
  AppCol,
  AppFormLabel,
} from '@/components/atoms';

interface FreeMedicalCategoryEditModalProps {
  isOpen: boolean;
  category: FreeMedicalTreatmentCategory | null;
  isNew: boolean;
  initData: FreeMedicalInitData | null;
  onClose: () => void;
  onSave: (data: CategoryFormData & { sort_number: number }) => Promise<void>;
}

interface FormDataState extends CategoryFormData {
  sort_number: number;
}

const FreeMedicalCategoryEditModal: React.FC<FreeMedicalCategoryEditModalProps> = ({
  isOpen,
  category,
  isNew,
  initData,
  onClose,
  onSave,
}) => {
  const toast = useAppToast();
  const modal = useAppModal();
  const [formData, setFormData] = useState<FormDataState>({
    category_name: '',
    // use 0 so the select shows the placeholder instead of a default value
    product_sale_type_id: 0,
    sort_number: 1,
  });

  // Track original sort number to detect changes (for A2 cascade logic)
  const [originalSortNumber, setOriginalSortNumber] = useState<number>(1);

  // Track initial form data to detect if form has unsaved changes
  const [initialFormData, setInitialFormData] = useState<FormDataState>({
    category_name: '',
    product_sale_type_id: 0,
    sort_number: 1,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  /**
   * Initialize form data when modal opens
   * Matches A2 behavior: populate with existing data or default to new entry
   */
  useEffect(() => {
    if (isOpen) {
      if (category && !isNew) {
        // EDIT MODE: Load existing category data
        const loadedData: FormDataState = {
          category_name: category.category_name || '',
          product_sale_type_id: category.product_sale_type_id || 1,
          sort_number: category.sort_number || 1,
        };
        setFormData(loadedData);
        setInitialFormData(loadedData);
        setOriginalSortNumber(category.sort_number || 1);
      } else {
        // CREATE MODE: Default to next available position
        const nextSortNumber = (initData?.categories?.length || 0) + 1;
        const defaultData: FormDataState = {
          category_name: '',
          // start with no type selected so placeholder is shown
          product_sale_type_id: 0,
          sort_number: nextSortNumber,
        };
        setFormData(defaultData);
        setInitialFormData(defaultData);
        setOriginalSortNumber(nextSortNumber);
      }
      setErrors({});
    } else {
      const defaultData: FormDataState = {
        category_name: '',
        product_sale_type_id: 0,
        sort_number: 1,
      };
      setFormData(defaultData);
      setInitialFormData(defaultData);
    }
  }, [isOpen, category, isNew, initData]);

  const { confirmIfChanged, clearFormChange } = useFormChangeGuard(formData, initialFormData);

  /**
   * Validate form data using Zod schema
   * Returns validation result and updates error state
   */
  const validateForm = useCallback((): boolean => {
    const validation = freeMedicalCategorySchema.safeParse(formData);

    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      const flat = validation.error.flatten().fieldErrors;
      Object.entries(flat).forEach(([key, arr]) => {
        if (arr && arr.length) {
          fieldErrors[key] = arr[0] as string;
        }
      });
      setErrors(fieldErrors);
      return false;
    }

    return true;
  }, [formData]);

  /**
   * Handle form submission
   * Matches A2 workflow:
   * 1. Validate all fields
   * 2. Show confirmation dialog (if changes made)
   * 3. Submit to API with sort_number for cascade handling
   * 4. Handle errors gracefully
   */
  const handleSubmit = async () => {
    // Validation step
    if (!validateForm()) {
      return;
    }

    // Check if sort position changed (A2 handles cascade updates on backend)
    const sortPositionChanged = !isNew && originalSortNumber !== formData.sort_number;

    // Show confirmation if sort position changed (matching A2 UX)
    if (sortPositionChanged) {
      const confirmMessage = `並び順を変更します。よろしいですか？\n\n現在: ${originalSortNumber} → 変更後: ${formData.sort_number}`;
      const confirmed = await new Promise<boolean>(resolve => {
        modal.confirm({
          title: '確認',
          content: confirmMessage,
          okText: 'はい',
          cancelText: 'いいえ',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) {
        return;
      }
    }

    setSaving(true);
    setErrors({});

    try {
      // Send all required fields including sort_number to backend
      // Backend will handle cascade updates if sort_number changed
      await onSave(formData);
      clearFormChange();
      onClose();
    } catch (err: unknown) {
      const fallback = 'データが見つかりません。';
      if (err && typeof err === 'object') {
        if ('errors' in err) {
          const errorData = err as { errors: Record<string, string | string[]> };
          setErrors(
            Object.fromEntries(
              Object.entries(errorData.errors).map(([k, v]) => [
                k,
                Array.isArray(v) ? v[0] : String(v),
              ])
            ) as Record<string, string>
          );
          const first = Object.values(errorData.errors)[0];
          toast.error(Array.isArray(first) ? first[0] : String(first ?? fallback));
        } else if ('message' in err) {
          const errorMsg = err as { message: string };
          toast.error(errorMsg.message || fallback);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle field changes and update form state
   * Clear related errors when user starts editing
   */
  const handleCategoryNameChange = useCallback(
    (value: string) => {
      setFormData(prev => ({ ...prev, category_name: value }));
      if (errors.category_name) {
        setErrors(prev => ({ ...prev, category_name: '' }));
      }
    },
    [errors.category_name]
  );

  const handleTypeChange = useCallback(
    (value: unknown) => {
      if (value === undefined || value === null || value === '') {
        setFormData(prev => ({ ...prev, product_sale_type_id: 0 }));
      } else {
        const numValue = typeof value === 'number' ? value : Number(value);
        setFormData(prev => ({ ...prev, product_sale_type_id: !isNaN(numValue) ? numValue : 0 }));
      }
      if (errors.product_sale_type_id) {
        setErrors(prev => ({ ...prev, product_sale_type_id: '' }));
      }
    },
    [errors.product_sale_type_id]
  );

  const handleSortChange = useCallback(
    (value: unknown) => {
      if (value === undefined || value === null || value === '') {
        setFormData(prev => ({ ...prev, sort_number: 0 }));
      } else {
        const numValue = typeof value === 'number' ? value : Number(value);
        setFormData(prev => ({ ...prev, sort_number: !isNaN(numValue) ? numValue : 0 }));
      }
      if (errors.sort_number) {
        setErrors(prev => ({ ...prev, sort_number: '' }));
      }
    },
    [errors.sort_number]
  );

  /**
   * Generate sort number options
   * In A2: for create mode, shows existing numbers + next position
   * In edit mode: shows existing numbers (excluding current if not moving)
   */
  const generateSortOptions = useCallback(() => {
    const totalCategories = initData?.categories?.length || 0;
    const maxSortNumber = totalCategories + 1;

    return Array.from({ length: maxSortNumber }, (_, i) => i + 1).map(n => ({
      label: String(n),
      value: n,
    }));
  }, [initData?.categories?.length]);

  const handleClose = () => {
    confirmIfChanged(() => {
      clearFormChange();
      onClose();
    });
  };

  // reset form data to original values or defaults
  const handleClear = () => {
    confirmIfChanged(() => {
      const nextSort = (initData?.categories?.length || 0) + 1;
      const clearedSortNumber = isNew ? nextSort : category?.sort_number || formData.sort_number;
      const clearedData: FormDataState = {
        category_name: '',
        product_sale_type_id: 0,
        sort_number: clearedSortNumber,
      };

      const applyClear = () => {
        setFormData(clearedData);
        setOriginalSortNumber(clearedSortNumber);
        setInitialFormData(clearedData);
        clearFormChange();
        setErrors({});
      };

      // If the user already manually cleared everything to the same state,
      // don't show the discard-confirm dialog.
      if (deepCompare(formData, clearedData)) {
        applyClear();
        return;
      }

      applyClear();
    });
  };

  // Generate product type options for select
  const typeOptions = (initData?.product_sale_types || []).map(type => ({
    label: type.type_name,
    value: type.id,
  }));

  const sortOptions = generateSortOptions();

  // Modal title with context (edit vs create)
  const modalTitle = (
    <span>
      {isNew ? 'カテゴリ登録' : 'カテゴリ編集'}
      {/* {!isNew && category && ` (ID: ${category.sub_number})`} */}
    </span>
  );

  const modalFooter = [
    <AppButton key="clear" onClick={handleClear} disabled={saving}>
      クリア
    </AppButton>,
    <AppButton key="save" onClick={handleSubmit} disabled={saving} type="primary">
      {saving ? '保存中...' : '保存'}
    </AppButton>,
  ];

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="max-w-lg"
      title={modalTitle}
      footer={modalFooter}
    >
      <AppRow gutter={[16, 16]}>
        {/* Sort Number Field */}
        <AppCol span={24} hidden>
          <AppRow gutter={16} align="middle">
            <AppCol span={8}>
              <AppFormLabel label="並び順" htmlFor="category_sort" required />
            </AppCol>
            <AppCol span={16}>
              <div>
                <AppSelect
                  id="category_sort"
                  className="w-full"
                  value={formData.sort_number > 0 ? formData.sort_number : undefined}
                  onChange={handleSortChange}
                  status={errors.sort_number ? 'error' : undefined}
                  options={sortOptions}
                />
                {errors.sort_number && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {errors.sort_number}
                  </p>
                )}
              </div>
            </AppCol>
          </AppRow>
        </AppCol>

        {/* ID Field - Read-only in Edit Mode */}
        {!isNew && (
          <AppCol span={24} hidden>
            <AppRow gutter={16} align="middle">
              <AppCol span={8}>
                <AppFormLabel label="ID" />
              </AppCol>
              <AppCol span={16}>
                <div className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded">
                  {category?.sub_number || '-'}
                </div>
              </AppCol>
            </AppRow>
          </AppCol>
        )}

        {/* Category Name Input Field */}
        <AppCol span={24}>
          <AppRow gutter={16} align="middle">
            <AppCol span={8}>
              <AppFormLabel label="カテゴリ名" required htmlFor="category_name" />
            </AppCol>
            <AppCol span={16}>
              <div>
                <AppTextField
                  id="category_name"
                  value={formData.category_name}
                  onChange={e => handleCategoryNameChange(e.target.value)}
                  status={errors.category_name ? 'error' : undefined}
                  maxLength={255}
                  placeholder="カテゴリ名を入力してください"
                />
                {errors.category_name && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {errors.category_name}
                  </p>
                )}
              </div>
            </AppCol>
          </AppRow>
        </AppCol>

        {/* Product Sale Type/区分 Select Field */}
        <AppCol span={24}>
          <AppRow gutter={16} align="middle">
            <AppCol span={8}>
              <AppFormLabel label="区分" required htmlFor="category_type" />
            </AppCol>
            <AppCol span={16}>
              <div>
                <AppSelect
                  id="category_type"
                  className="w-full"
                  value={
                    formData.product_sale_type_id > 0 ? formData.product_sale_type_id : undefined
                  }
                  onChange={handleTypeChange}
                  status={errors.product_sale_type_id ? 'error' : undefined}
                  options={typeOptions}
                />
                {errors.product_sale_type_id && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {errors.product_sale_type_id}
                  </p>
                )}
              </div>
            </AppCol>
          </AppRow>
        </AppCol>
      </AppRow>
    </AppModal>
  );
};

export default FreeMedicalCategoryEditModal;
